import React, { createContext, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteBooking, restoreBooking, updateBooking as updateBookingAction } from '../../actions/bookingActions';
import { DATE_FORMATS, formatDate } from '@hub-mono/utils';
import { Icon, IconButton, Tooltip } from '@hub-mono/ui';
import { selectCompanyApprovers } from '../../selectors/company';
import { selectMappedResourceGroups } from '../../selectors/resourceGroup';
import { WAITING_FOR_APPROVAL } from '../../enums/bookingTypeEnum';
import { getFormDefaultApprovers } from '../request/utils/getFormDefaultApprovers';
import { getResourcesMap } from '../../selectors/resource';
import { UndoService, UNDO_ACTIONS } from '@hub-mono/app-modules';
import { SelectionButton } from './components/header/schedulerNav/styles';
import styled, { css } from 'styled-components';
import { selectModals } from '../../selectors/modal';

export const undoService = new UndoService();

const StyledUndoButtonNav = styled(SelectionButton)`
    margin-left: 0.5rem;

    ${props => {
        if (props.disabled) {
            return css`
                &.btn.disabled {
                    pointer-events: none;
                }
            `;
        }
    }}
`;

export const UndoButtonNav = () => {
    const { canUndo, onUndo } = useUndoButtonContext();
    return (
        <Tooltip title="Undo" placement="top">
            <StyledUndoButtonNav onClick={onUndo} disabled={!canUndo} data-cy="scheduler-undo-button-nav">
                <Icon name="undo" size={20} color={canUndo ? undefined : '#00000042'} />
            </StyledUndoButtonNav>
        </Tooltip>
    );
};

export const UndoButtonFooter = () => {
    const { canUndo, onUndo } = useUndoButtonContext();

    if (!canUndo) {
        return null;
    }

    return (
        <Tooltip title="Undo">
            <IconButton onClick={onUndo} icon="undo" color="on-primary" dataCy="scheduler-footer-undo" />
        </Tooltip>
    );
};

const UndoLogicContext = createContext({});

export const UndoLogicProvider = ({ children }) => {
    const dispatch = useDispatch();
    const modals = useSelector(selectModals);

    const [canUndo, setCanUndo] = React.useState(false);

    const approvers = useSelector(selectCompanyApprovers);
    const mappedResourceGroups = useSelector(selectMappedResourceGroups);
    const resourcesMap = useSelector(getResourcesMap);

    const handleUndo = useCallback(() => {
        const action = undoService.undo();

        if (!action) {
            return;
        }

        switch (action.type) {
            case UNDO_ACTIONS.CREATE:
                dispatch(
                    deleteBooking.request({ _id: action.createdBookingId }, undefined, undefined, { isUndo: true })
                );
                break;
            case UNDO_ACTIONS.MOVE: {
                const payload = {
                    _id: action.bookingId,
                    resource: action.booking.resource,
                    project: action.booking.project,
                    start: formatDate(action.booking.start, DATE_FORMATS.BE_FORMAT),
                    end: formatDate(action.booking.end, DATE_FORMATS.BE_FORMAT),
                    deadlineDate: formatDate(action.booking.deadlineDate, DATE_FORMATS.BE_FORMAT),
                    deadlineName: action.booking.deadlineName,
                };

                const resource = resourcesMap[action.booking.resource];

                if (action.booking.type === WAITING_FOR_APPROVAL.value) {
                    const defaultApprovers = getFormDefaultApprovers({
                        approvers,
                        resource,
                        mappedResourceGroups,
                    }).map(approver => ({ id: approver._id }));
                    payload.approvalInfo = {
                        approvers: defaultApprovers,
                        requesterNote: action.booking.approvalInfo?.requesterNote,
                    };
                }

                dispatch(updateBookingAction.request(action.bookingId, payload, { isUndo: true }));
                break;
            }
            case UNDO_ACTIONS.RESIZE:
                dispatch(
                    updateBookingAction.request(
                        action.bookingId,
                        {
                            _id: action.bookingId,
                            start: formatDate(action.booking.start, DATE_FORMATS.BE_FORMAT),
                            end: formatDate(action.booking.end, DATE_FORMATS.BE_FORMAT),
                            deadlineDate: formatDate(action.booking.deadlineDate, DATE_FORMATS.BE_FORMAT),
                            deadlineName: action.booking.deadlineName,
                        },
                        {
                            isUndo: true,
                        }
                    )
                );
                break;
            case UNDO_ACTIONS.DELETE:
                dispatch(restoreBooking.request(action.bookingId, { isUndo: true }));
                break;
            case UNDO_ACTIONS.CUT_PASTE:
                dispatch(
                    deleteBooking.request({ _id: action.createdBookingId }, undefined, undefined, { isUndo: true })
                );
                dispatch(restoreBooking.request(action.deletedBookingId, { isUndo: true }));
                break;
        }
    }, [approvers, dispatch, mappedResourceGroups, resourcesMap]);

    const handleKeyboardShortcuts = useCallback(
        event => {
            if (modals?.length) {
                return;
            }

            if (!canUndo) {
                return;
            }

            const key = event.keyCode ? event.keyCode : event.which;
            if (key === 90 && (event.metaKey || event.ctrlKey)) {
                handleUndo();
            }
        },
        [canUndo, handleUndo, modals?.length]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardShortcuts, true);

        return () => {
            document.removeEventListener('keydown', handleKeyboardShortcuts, true);
        };
    }, [handleKeyboardShortcuts]);

    const handleUndoChanges = useCallback(({ canUndo }) => {
        setCanUndo(canUndo);
    }, []);

    useEffect(() => {
        const listenerId = undoService.addListener(handleUndoChanges);

        return () => {
            undoService.removeListener(listenerId);
        };
    }, [handleUndoChanges]);

    return <UndoLogicContext.Provider value={{ canUndo, onUndo: handleUndo }}>{children}</UndoLogicContext.Provider>;
};

const useUndoButtonContext = () => {
    return useContext(UndoLogicContext);
};
